import React, { useState } from 'react';
import { AgentPromptBody } from './AskYourAgent';
import { showInkToast } from './InkToast';
import { Finding, InkSection } from './shared';

/**
 * Content → Design → Checks (UX review round 8, Prototype v15): the site's
 * custom CSS that leans on the template's markup, in one plain line — how many
 * rules may break if the template changes — with the fix as a prompt for the
 * agent and the rules themselves behind Show details, each with its reason in
 * words. The selectors are the agent's business; the person sees them only on
 * asking.
 */
export function CssChecks({
  findings,
  templateName,
}: {
  findings: Finding[];
  templateName: string | null;
}) {
  const [ask, setAsk] = useState(false);
  const [details, setDetails] = useState(false);
  const rules = findings.filter((f) => f.selector);
  // The site document lists the first few, then "… and N more".
  const count = rules.length + findings.reduce((n, f) => n + (f.more || 0), 0);
  if (!count) return null;

  const rulesWord = count === 1 ? 'custom CSS rule' : 'custom CSS rules';
  const layout = templateName ? `the ${templateName} template’s layout` : 'your template’s layout';
  const prompt =
    `Rewrite the ${count} ${rulesWord} on my site that depend on ${layout} so they work ` +
    'with any template. Keep the site looking the same, and send the change to me for approval ' +
    'in tinycms.';

  return (
    <InkSection
      id="checks"
      title="Checks"
      description="What could go wrong with your site’s look. Nothing here is broken for readers today."
    >
      <div className="ink-css-check">
        <div className="ink-css-check-head">
          <span className="ink-css-check-dot" />
          <div className="ink-css-check-text">
            <div className="ink-css-check-title">
              {count} {rulesWord} may break if you change template
            </div>
            <div className="ink-site-row-desc">
              {count === 1 ? 'It depends' : 'They depend'} on how {templateName || 'your template'}{' '}
              lays out the page. Another template could move or hide what{' '}
              {count === 1 ? 'it styles' : 'they style'}.
            </div>
          </div>
          <span className="ink-css-check-tag">Warning</span>
        </div>
        <div className="ink-css-check-actions">
          <button
            type="button"
            className="ink-btn"
            aria-expanded={ask}
            onClick={() => setAsk((v) => !v)}
          >
            Ask your agent to fix
          </button>
          <button
            type="button"
            className="ink-site-link"
            aria-expanded={details}
            onClick={() => setDetails((v) => !v)}
          >
            {details ? 'Hide details' : 'Show details'}
          </button>
        </div>
        {ask ? (
          <div className="ink-css-check-ask">
            <AgentPromptBody
              prompt={prompt}
              note="Copy this prompt into your agent. The fix comes back here for you to approve."
              onCopied={() => {
                setAsk(false);
                showInkToast('Prompt copied. Paste it into your agent.');
              }}
            />
          </div>
        ) : null}
        {details ? (
          <ul className="ink-css-check-rules">
            {rules.map((f) => (
              <li key={f.selector}>
                <code>{f.selector}</code>
                <span>{f.why || 'May not survive a change of template.'}</span>
              </li>
            ))}
            {findings.some((f) => f.more) ? (
              <li>
                <span>
                  …and {findings.reduce((n, f) => n + (f.more || 0), 0)} more. Your agent sees them
                  all.
                </span>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </InkSection>
  );
}
